// components/agent/employees/AgentProfileCardForAgent.jsx
import { useEffect, useRef, useState } from "react";
import { FiCheckCircle, FiMoreVertical, FiLoader } from "react-icons/fi";
import { MdFaceUnlock, MdPhone, MdEmail } from "react-icons/md";
import { RiTeamFill } from "react-icons/ri";
import { RxCross2 } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAgent,
  getAgentApplicationCount,
  deactivateAgent,
  clearApplicationCount
} from "../../../redux/features/employeeProfileSlice";
import CreateEmployeeForm from "../../admin/employees/CreateEmployee.jsx";
import { updateAgentInList, fetchAgents } from "../../../redux/features/agentSlice.js";



export default function AgentProfileCardForAgent({ agent }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const menuRef = useRef(null);
  


  const [formData, setFormData] = useState({
    title: "",
    message: "",
    agentEmail: agent.email || "",
  });

  // Ensure isActive defaults to true if not explicitly set to false
  const isAgentActive = agent.isActive !== false;
  const isSubAdmin = agent.role === 'sub-admin' || agent.role === 'subadmin' || agent.role === 'admin';

  const { agents } = useSelector((state) => state.agents);
  const { applicationCount, isDeactivating } = useSelector((state) => state.employeeProfile);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const closeModel = () => {
    setIsModalOpen(false);
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.message || !formData.agentEmail) {
      alert("Please fill all fields");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("No token found. Please log in again.");
      return;
    }

    try {
      setIsSubmittingMessage(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/sendMessageToAgent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agentEmail: formData.agentEmail,
            title: formData.title,
            message: formData.message,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setSuccess(true);
      setFormData({ agentEmail: agent.email || "", title: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const handleEditAgent = (formData) => {
    // Don't allow editing email and role
    const { email, role, ...updateData } = formData;
    dispatch(updateAgent({ agentId: agent._id, formData: updateData }))
      .unwrap()
      .then((result) => {
        // Also update the agent in the agents list for grid view
        dispatch(updateAgentInList(result.updatedAgent));
        setIsEditModalOpen(false);
      })
      .catch((error) => {
        console.error("Failed to update agent:", error);
        alert("Failed to update agent. Please try again.");
      });
  };

  const handleDeactivateClick = () => {
    setIsMenuOpen(false);
    // Only fetch application count if we're deactivating (not reactivating)
    if (isAgentActive) {
      dispatch(getAgentApplicationCount(agent._id));
    }
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivateConfirm = () => {
    setIsDeactivateModalOpen(false);
    // Only show transfer modal if agent is active (deactivating)
    if (isAgentActive) {
      setIsTransferModalOpen(true);
    } else {
      // Reactivating - no transfer needed
      handleFinalAction();
    }
  };

  const handleFinalAction = () => {
    // If agent is inactive, we're reactivating (no transfer needed)
    if (!isAgentActive) {
      dispatch(deactivateAgent({ agentId: agent._id, newAgentId: null }))
        .unwrap()
        .then((result) => {
          // Update the agent in the list instead of removing
          const updatedAgent = result.data || { ...agent, isActive: true };
          dispatch(updateAgentInList(updatedAgent));
          setIsTransferModalOpen(false);
          dispatch(clearApplicationCount());
          setSelectedAgentId("");
        })
        .catch((error) => {
          console.error("Failed to reactivate agent:", error);
          alert("Failed to reactivate agent. Please try again.");
        });
      return;
    }

    // If there are applications, require agent selection
    if (applicationCount > 0) {
      if (!selectedAgentId) {
        alert("Please select an agent to transfer applications to");
        return;
      }
      // Transfer applications and deactivate
      dispatch(deactivateAgent({ agentId: agent._id, newAgentId: selectedAgentId }))
        .unwrap()
        .then((result) => {
          // Update the agent in the list instead of removing
          const updatedAgent = result.data || { ...agent, isActive: false };
          dispatch(updateAgentInList(updatedAgent));
          setIsTransferModalOpen(false);
          dispatch(clearApplicationCount());
          setSelectedAgentId("");
        })
        .catch((error) => {
          console.error("Failed to deactivate agent:", error);
          alert("Failed to deactivate agent. Please try again.");
        });
    } else {
      // No applications, just deactivate
      dispatch(deactivateAgent({ agentId: agent._id, newAgentId: null }))
        .unwrap()
        .then((result) => {
          // Update the agent in the list instead of removing
          const updatedAgent = result.data || { ...agent, isActive: false };
          dispatch(updateAgentInList(updatedAgent));
          setIsTransferModalOpen(false);
          dispatch(clearApplicationCount());
          setSelectedAgentId("");
        })
        .catch((error) => {
          console.error("Failed to deactivate agent:", error);
          alert("Failed to deactivate agent. Please try again.");
        });
    }
  };

  const availableAgents = agents.filter(a => a._id !== agent._id && (a.isActive !== false));



  return (
    <>
      {/* Agent Card */}
      <div className={`relative p-4 rounded-2xl text-white max-w-xl h-full flex flex-col ${
        isAgentActive ? 'bg-[#005B96]' : 'bg-gray-400'
      }`}>
        {/* Status badges */}
        <div className="absolute -top-3 left-4 flex gap-2">
          {!isAgentActive && (
            <div className="bg-gray-600 text-white text-xs px-3 py-1 rounded-full shadow-lg">
              Inactive
            </div>
          )}
          {isSubAdmin && (
            <div className="bg-[#00bbff] text-white text-xs px-3 py-1 rounded-full shadow-lg border border-white">
              Sub Admin
            </div>
          )}
        </div>

        {/* Three dots menu */}
        {agent.role !== 'sub-admin' && agent.role !== 'admin' && 
          <div ref={menuRef} className="absolute top-3 right-3">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-1 rounded-full transition-colors ${
                isAgentActive ? 'hover:bg-[#004274]' : 'hover:bg-gray-500'
                }`}
          >
            <FiMoreVertical className="w-5 h-5" />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 mt-1 w-52 bg-white rounded-md shadow-lg py-1 z-10">
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsEditModalOpen(true);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Edit Employee
              </button>
              <button
                onClick={handleDeactivateClick}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {isAgentActive ? 'Deactivate Employee' : 'Reactivate Employee'}
              </button>
            </div>
          )}
        </div>}

        {/* Profile section */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
            <MdFaceUnlock className="w-12 h-12 text-gray-800" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold max-w-[220px] whitespace-normal break-words">
              {agent.firstName} {agent.lastName}
            </h3>
            <p className="text-sm text-gray-200 max-w-[220px] whitespace-normal break-words">
              {agent.country || "N/A"}
            </p>
          </div>
        </div>

        {/* Contact information */}
        <div className="space-y-2 mb-6 text-xs">
          <div className="flex items-center gap-3">
            <MdPhone className="w-5 h-5" />
            <span>{agent.phone}</span>
          </div>
          <div className="flex items-center gap-3">
            <MdEmail className="w-5 h-5" />
            <span>{agent.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <RiTeamFill className="w-5 h-5" />
            {agent.team && <span>{agent.team}</span>}
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-auto flex flex-row gap-2 text-xs">
          <button
            onClick={() => setIsModalOpen(true)}
            disabled={!isAgentActive}
            className={`rounded-full py-2 w-full px-4 font-medium transition-colors ${
              isAgentActive
                ? 'bg-white text-gray-900 hover:bg-gray-100'
                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
            }`}
          >
            Message
          </button>
          <button
            onClick={() => navigate(`/agent/employees/${agent._id}`)}
            className="rounded-full w-full bg-white text-gray-900 px-4 py-2 font-medium hover:bg-gray-100 transition-colors"
          >
            View Profile
          </button>
        </div>
      </div>

      {/* Message Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 text-sm md:text-base">
          <div className="bg-white rounded-lg w-full max-w-md relative">
            <div className="flex items-center justify-end pr-4 pt-4">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSuccess(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <RxCross2 size={20} color="black" />
              </button>
            </div>

            {success ? (
              <div className="p-12 w-full max-w-md text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCheckCircle className="h-16 w-16 text-[#036494]" />
                </div>
                <h2 className="text-base font-medium mb-6">
                  Your message has been successfully delivered to the agent.
                </h2>
                <button
                  onClick={closeModel}
                  className="w-full bg-[#036494] text-white py-2 rounded-full hover:bg-[#075985] transition-colors"
                >
                  Back
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 mb-6 space-y-4">
                <div className="space-y-2">
                  <label className="block text-[#1E1E1E]">Agent Email</label>
                  <input
                    type="email"
                    name="agentEmail"
                    value={formData.agentEmail}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[#1E1E1E]">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[#1E1E1E]">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmittingMessage}
                  className={`w-full text-white py-2 rounded-full transition-colors flex items-center justify-center gap-2 ${
                    isSubmittingMessage
                      ? 'bg-[#036494]/70 cursor-not-allowed'
                      : 'bg-[#036494] hover:bg-[#075985]'
                  }`}
                >
                  {isSubmittingMessage ? (
                    <>
                      <FiLoader className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      <CreateEmployeeForm
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        onSubmit={handleEditAgent}
        agent={agent}
        isEdit={true}
      />

      {/* Deactivation/Reactivation Confirmation Modal */}
      {isDeactivateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                setIsDeactivateModalOpen(false);
                dispatch(clearApplicationCount());
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <RxCross2 size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4 text-gray-900">
              {isAgentActive ? 'Deactivate Employee' : 'Reactivate Employee'}
            </h2>
            <p className="mb-6 text-gray-700">
              {isAgentActive ? (
                <>
                  Are you sure you want to deactivate this agent?
                  {applicationCount !== null && applicationCount > 0 && (
                    <span className="font-semibold"> This agent is working on {applicationCount} application{applicationCount > 1 ? 's' : ''}.</span>
                  )}
                </>
              ) : (
                'Are you sure you want to reactivate this agent?'
              )}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeactivateModalOpen(false);
                  dispatch(clearApplicationCount());
                }}
                className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateConfirm}
                className={`px-4 py-2 text-white rounded-full ${
                  isAgentActive
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isAgentActive ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Applications Modal */}
      {isTransferModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                setIsTransferModalOpen(false);
                dispatch(clearApplicationCount());
                setSelectedAgentId("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <RxCross2 size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4 text-gray-900">
              {applicationCount > 0 ? 'Transfer Applications' : 'Deactivate Employee'}
            </h2>
            {applicationCount > 0 ? (
              <>
                <p className="mb-4 text-gray-700">
                  Select a new agent to transfer the {applicationCount} application{applicationCount > 1 ? 's' : ''} to:
                </p>

                <select
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md mb-6"
                >
                  <option value="">Select an agent</option>
                  {availableAgents.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.firstName} {agent.lastName}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <p className="mb-6 text-gray-700">This agent has no active applications. You can proceed to deactivate.</p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsTransferModalOpen(false);
                  dispatch(clearApplicationCount());
                  setSelectedAgentId("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalAction}
                disabled={applicationCount > 0 && (!selectedAgentId || isDeactivating)}
                className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeactivating ? "Processing..." : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}








    </>
  );
}